import { Button, Flex, Link, theme } from '@everlywell/ui-kit';
import { isUndefined } from 'lodash-es';
import React, { MouseEvent, useRef } from 'react';
import {
  parseAnchorId,
  scrollXToTargetIntoView,
  scrollYToTargetAdjusted,
} from 'utils/helpers/scrollHelpers';
import { useUserAgent } from 'utils/hooks/';

import {
  DESKTOP_NAV_BAR_HEIGHT,
  MOBILE_NAV_BAR_HEIGHT,
  NavigationAnchorProps,
  TileProps,
} from '../NavigationAnchor';
import { useStickyContext } from '../Provider/StickyProvider';

export type StickyAnchorsProps = NavigationAnchorProps;

const backgroundActiveButtonColor = {
  backgroundColor: 'tints.darkCream',
};

/**
 * StickyAnchors component will stick to the top of the screen and display the tiles passed in as props
 */
const StickyAnchors = (props: StickyAnchorsProps) => {
  const { areAppleDevicesPresent } = useUserAgent();
  const { isNavVisible, activeAnchor, anchorsHasMap } = useStickyContext();
  const stickyRef = useRef<HTMLDivElement>(null);

  if (!props) {
    return null;
  }
  const tiles = props.tiles.filter((tile) => !tile.mobileOnly);

  if (!isUndefined(activeAnchor)) {
    const anchor = parseAnchorId(activeAnchor);

    scrollXToTargetIntoView({
      id: anchorsHasMap[anchor]?.toString(),
      areAppleDevicesPresent,
      leftOffset: 48,
    });
  }

  const onClick = (e: MouseEvent<HTMLButtonElement>, anchor = '') => {
    e.preventDefault();
    scrollYToTargetAdjusted({
      id: anchor,
      areAppleDevicesPresent,
      topOffset: 130,
    });
  };
  return (
    <Flex
      data-testid="sticky-anchors"
      id="sticky-anchors"
      ref={stickyRef}
      alignSelf="flex-start"
      backgroundColor="tints.white"
      borderBottom={`1px solid ${theme.colors.tints.cream}`}
      gap={2}
      opacity={isNavVisible ? 1 : 0}
      overflow="auto"
      padding={`${theme.space[3]} ${theme.space[4]}`}
      paddingLeft={[4, null, 12]}
      position="fixed"
      top={[`${MOBILE_NAV_BAR_HEIGHT}px`, null, `${DESKTOP_NAV_BAR_HEIGHT}px`]}
      transition="opacity 0.3s ease-in-out"
      width="100vw"
      zIndex="2"
    >
      {tiles.map((tile, index) => (
        <Anchor
          index={index}
          activeAnchor={activeAnchor}
          key={`sticky-anchor-${index}`}
          onClick={onClick}
          tile={tile}
        />
      ))}
    </Flex>
  );
};

export default StickyAnchors;

type AnchorType = {
  index: number;
  tile: TileProps;
  onClick: (
    e: MouseEvent<HTMLButtonElement>,
    anchor: string | undefined,
  ) => void;
  activeAnchor?: string;
};
function Anchor({
  activeAnchor: unparsedActiveAnchor,
  onClick,
  tile,
}: AnchorType) {
  const { setAnchorsHasMap } = useStickyContext();

  const activeAnchor = parseAnchorId(unparsedActiveAnchor || '');
  const anchor = parseAnchorId(tile.anchor || '');
  const tileId = parseAnchorId(tile.id || '');
  const isActive = activeAnchor === anchor;
  const id = tileId || `anchor-${anchor}`;

  setAnchorsHasMap((prev) => {
    if (anchor in prev) {
      return prev;
    }
    const anchors = {
      ...prev,
      [anchor]: id,
    };
    return anchors;
  });

  const isHover = isActive ? 'tints.darkCream' : 'unset';

  return (
    <Button
      _active={backgroundActiveButtonColor}
      _hover={{ backgroundColor: isHover, textDecoration: 'none' }}
      as={Link}
      color="tints.darkGrey"
      colorScheme="whiteCS"
      data-testid={`sticky-anchor-cta`}
      fontSize={theme.fontSizes['md']}
      href={tile.anchor}
      id={id}
      isActive={isActive}
      onClick={(e) => onClick(e, tile.anchor)}
      paddingX={[3, null, 4]}
      paddingY="2"
      transition="background-color 0.3s ease-in-out"
      width="auto"
      height={['28px', null, '32px']}
    >
      {tile.title}
    </Button>
  );
}
